import { useState, useRef, useEffect } from "react";
import { Play } from "lucide-react";

// Lazy embed: shows a poster + play button, loads the iframe only on demand.
// No audio autoplay until user interacts.
export function LazyEmbed({ type, id, url, poster, title, testId }) {
  const [active, setActive] = useState(false);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  let src = "";
  if (type === "youtube") {
    src = `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&autoplay=1`;
  } else if (type === "soundcloud") {
    src = `https://w.soundcloud.com/player/?url=${encodeURIComponent(
      url
    )}&color=%23D48C70&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=true`;
  }

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden rounded-2xl bg-sand"
      style={{ aspectRatio: type === "youtube" ? "16 / 9" : "16 / 7" }}
      data-testid={testId}
    >
      {active && inView ? (
        <iframe
          className="absolute inset-0 h-full w-full"
          src={src}
          title={title}
          frameBorder="0"
          loading="lazy"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => setActive(true)}
          className="group absolute inset-0 flex h-full w-full items-center justify-center"
          aria-label={`Play ${title}`}
          data-testid={testId ? `${testId}-play` : undefined}
        >
          {poster && (
            <img
              src={poster}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          )}
          <span className="absolute inset-0 bg-charcoal/30" />
          <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-cream/90 text-charcoal shadow-xl transition-transform duration-300 group-hover:scale-110">
            <Play className="ml-1 h-7 w-7 fill-current" />
          </span>
        </button>
      )}
    </div>
  );
}
